import { useQuery } from "@tanstack/react-query";
import { useLocale, useTranslations } from "use-intl";

import { ExternalArrowIcon, StoriesIcon } from "@/components/appIcons";
import { Link } from "@/components/link";
import { cn } from "@/lib/utils";
import { buildCdnRenderPath } from "@garimpa/lib/buildCdnRenderPath";
import { type FeaturePageEntityType } from "@garimpa/lib/featurePage";
import { type ViewerLocale } from "@garimpa/lib/viewerPreferences";

import { getFeaturePageBacklinksQueryOptions } from "./feature-page-backlinks-query";

export function FeaturePageBacklinks({
  catalogId,
  entityType,
  workId,
}: {
  catalogId: string;
  entityType: FeaturePageEntityType;
  workId?: string;
}) {
  const locale = useLocale() as ViewerLocale;
  const t = useTranslations("globals.featurePages");
  const backlinksQuery = useQuery(
    getFeaturePageBacklinksQueryOptions({ entityType, catalogId, workId, locale }),
  );
  const collections = backlinksQuery.data?.collections ?? [];
  const backlinks = backlinksQuery.data?.editorialPages ?? [];
  if (backlinks.length === 0 && collections.length === 0) return null;

  const items = [
    ...collections.map((page) => ({
      ...page,
      kind: "collection" as const,
      family: "collection" as const,
    })),
    ...backlinks.map((page) => ({ ...page, kind: "editorial" as const })),
  ];
  const isSingleItem = items.length === 1;

  return (
    <section
      aria-labelledby="feature-page-backlinks-title"
      className="mb-6"
      data-testid="feature-page-backlinks"
    >
      <div>
        <div className="flex min-w-0 items-center gap-2.5">
          <div className="shrink-0 rounded-md bg-primary/10 p-1 text-primary">
            <StoriesIcon className="text-base" />
          </div>
          <div className="min-w-0">
            <div className="flex min-w-0 items-baseline gap-1.5">
              <h2 id="feature-page-backlinks-title" className="truncate text-sm font-bold">
                {t("relatedTitle")}
              </h2>
              {isSingleItem ? null : (
                <span className="shrink-0 text-xs text-muted-foreground">({items.length})</span>
              )}
            </div>
            {isSingleItem ? null : (
              <p className="mt-0.5 hidden truncate text-xs text-muted-foreground sm:block">
                {t("relatedDescription")}
              </p>
            )}
          </div>
        </div>

        <div
          className={cn(
            "mt-2",
            !isSingleItem &&
              "no-scrollbar flex snap-x snap-proximity touch-pan-x gap-2 overflow-x-auto overscroll-x-contain scroll-smooth",
          )}
          data-testid="feature-page-backlinks-row"
        >
          {items.map((page) => (
            <Link
              className={cn(
                "group flex items-center gap-2.5 rounded-lg bg-card px-3 py-2.5 transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                isSingleItem
                  ? "min-h-20 w-full"
                  : "min-h-16 w-[85%] max-w-72 shrink-0 snap-start md:w-auto md:max-w-none md:min-w-52 md:flex-1 md:basis-0",
              )}
              key={page.pageId}
              to={page.href}
            >
              {page.kind === "collection" ? (
                <img
                  className="size-9 shrink-0 rounded-md object-contain"
                  src={buildCdnRenderPath(page.emblemUrl, { width: 100, format: "webp" })}
                  alt={page.emblemLabel}
                  width={36}
                  height={36}
                />
              ) : null}
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-primary">
                  {t("familyLabel", { family: page.family })}
                </p>
                <h3 className="line-clamp-2 text-sm font-bold leading-snug group-hover:text-primary">
                  {page.title}
                </h3>
                {isSingleItem ? (
                  <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                    {page.description}
                  </p>
                ) : null}
              </div>
              <ExternalArrowIcon className="shrink-0 text-base text-foreground/60 transition-colors group-hover:text-primary" />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
